/**
 * FASE 11.2a — ContractArticlesModal: mostra il testo completo dei 13 articoli
 * delle Condizioni Generali D&O (Lloyd's) approvati dal Contraente ex art.
 * 1341-1342 c.c.
 *
 * Uso:
 *   const [open, setOpen] = useState(false);
 *   <ContractArticlesModal open={open} onOpenChange={setOpen} />
 *   <button onClick={() => setOpen(true)}>Leggi condizioni</button>
 */
import React from "react";
import { X } from "lucide-react";

const ARTICLES = [
  {
    art: "Art. 7",
    title: "Altre assicurazioni «secondo rischio»",
    body: "L'Assicurazione opera in eccesso rispetto ad altre coperture " +
          "assicurative valide ed efficaci alla stessa data che coprano il " +
          "medesimo rischio. Le altre coperture assicurative devono essere " +
          "prontamente comunicate all'Assicuratore.",
  },
  {
    art: "Art. 11",
    title: "Clausola Intermediario",
    body: "L'intermediario indicato in polizza opera per conto del Contraente. " +
          "L'Assicuratore riconosce che tutte le comunicazioni intercorse " +
          "tramite l'intermediario si intendono valide.",
  },
  {
    art: "Art. 12 (comma I)",
    title: "Assicurazione nella forma «claims made»",
    body: "La copertura è prestata nella forma \"claims made\", pertanto sono " +
          "coperti esclusivamente i Reclami presentati per iscritto per la " +
          "prima volta contro l'Assicurato durante il Periodo di " +
          "Assicurazione o durante il Periodo di Ultrattività eventualmente " +
          "concordato.",
  },
  {
    art: "Art. 14.b",
    title: "Responsabilità Solidale (Copertura sola quota di pertinenza)",
    body: "In caso di responsabilità solidale con altri soggetti, la copertura " +
          "opera solo per la quota di pertinenza della Persona Assicurata " +
          "individualmente considerata.",
  },
  {
    art: "Art. 16 e 17",
    title: "Periodo di Ultrattività (Massimali e Sottolimiti)",
    body: "Alla scadenza del contratto, l'Assicurato può richiedere un Periodo " +
          "di Ultrattività dietro pagamento di un premio aggiuntivo. Durante " +
          "tale periodo saranno coperti solo Reclami relativi ad atti " +
          "colposi commessi prima della scadenza. I massimali e sottolimiti " +
          "di indennizzo applicabili sono quelli del periodo originario.",
  },
  {
    art: "Art. 19 (voci a, b, c)",
    title: "Esclusione dei Reclami e circostanze già conosciute",
    body: "Sono esclusi dalla copertura i Reclami e le circostanze che erano " +
          "già conosciute dall'Assicurato prima dell'inizio del Periodo di " +
          "Assicurazione, o denunciate dopo la scadenza del Periodo di " +
          "Assicurazione o del Periodo di Ultrattività.",
  },
  {
    art: "Art. 19 (voce y)",
    title: "Esclusione dei Sindaci",
    body: "Salvo diversa specifica estensione contrattuale, la copertura non " +
          "opera per i membri del Collegio Sindacale della Società.",
  },
  {
    art: "Art. 19 (voce cc)",
    title: "Esclusione del rischio di fallimento",
    body: "Salvo diversa specifica estensione contrattuale, la copertura non " +
          "opera per Reclami originati da procedure fallimentari o di " +
          "insolvenza della Società.",
  },
  {
    art: "Art. 21",
    title: "Denuncia delle circostanze",
    body: "L'Assicurato è tenuto a denunciare per iscritto e tempestivamente " +
          "all'Assicuratore ogni Reclamo ricevuto e ogni circostanza che " +
          "possa ragionevolmente originare un Reclamo, indicando tutti gli " +
          "elementi utili.",
  },
  {
    art: "Art. 23 (comma II)",
    title: "Legali e consulenti nominati dalla Persona Assicurata",
    body: "La Persona Assicurata può nominare legali e consulenti di propria " +
          "fiducia previa consultazione con l'Assicuratore, il quale " +
          "riconoscerà i relativi costi entro i limiti stabiliti in polizza.",
  },
  {
    art: "Art. 24",
    title: "Gestione del Reclamo e obblighi relativi",
    body: "L'Assicurato è tenuto a cooperare pienamente con l'Assicuratore " +
          "nella gestione del Reclamo, fornendo ogni documento, informazione " +
          "e assistenza richiesti. Non deve riconoscere alcuna responsabilità " +
          "né effettuare pagamenti senza il consenso scritto dell'Assicuratore.",
  },
  {
    art: "Art. 26",
    title: "Facoltà bilaterale di recesso in caso di reclamo",
    body: "In caso di Reclamo, sia l'Assicurato sia l'Assicuratore hanno " +
          "facoltà di recedere dal contratto con preavviso scritto di 30 " +
          "giorni. Il recesso non pregiudica la copertura per i Reclami già " +
          "denunciati prima del recesso stesso.",
  },
  {
    art: "Art. 27",
    title: "Alienazione della Società (acquisizione dell'intero premio)",
    body: "In caso di alienazione, fusione o cessione della Società, la " +
          "copertura si intende automaticamente estesa fino alla scadenza " +
          "originaria per i soli Reclami relativi ad atti colposi commessi " +
          "prima della data di alienazione. L'intero premio si intende " +
          "definitivamente acquisito dall'Assicuratore.",
  },
];

export function ContractArticlesModal({ open, onOpenChange }) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="contract-articles-modal-backdrop"
      onClick={() => onOpenChange(false)}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
        data-testid="contract-articles-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-[#163321] text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="font-heading text-lg" data-testid="contract-articles-modal-title">
              Condizioni Generali di Assicurazione D&amp;O
            </h2>
            <p className="text-xs text-[#F0E7C8] mt-0.5">
              Articoli approvati specificamente ex art. 1341-1342 c.c.
            </p>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="p-2 rounded hover:bg-white/10 transition-colors"
            data-testid="contract-articles-modal-close"
            aria-label="Chiudi"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <p className="text-xs text-[#4A5E51] italic leading-relaxed">
            L'approvazione specifica dei seguenti articoli è richiesta dall'art.
            1341 e 1342 del Codice Civile per la validità delle clausole
            vessatorie contenute nelle Condizioni Generali di Assicurazione.
            Il testo integrale prevale sulle sintesi qui riportate.
          </p>
          {ARTICLES.map((a, idx) => (
            <div
              key={a.art}
              className="border-l-4 border-[#C9A448] pl-4 py-1"
              data-testid={`contract-article-${idx}`}
            >
              <div className="flex items-baseline gap-2 mb-1">
                <span className="font-bold text-[#163321] text-sm">
                  {a.art}
                </span>
                <span className="text-sm text-[#4A5E51]">— {a.title}</span>
              </div>
              <p className="text-xs text-[#0F1F14] leading-relaxed">{a.body}</p>
            </div>
          ))}
        </div>
        {/* Footer */}
        <div className="bg-[#F0E7C8]/40 px-6 py-3 border-t border-[#D9C49A] flex justify-end">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="px-5 py-2 rounded-lg bg-[#163321] text-white text-sm font-medium hover:bg-[#0F1F14] transition-colors"
            data-testid="contract-articles-modal-close-footer"
          >
            Ho compreso, chiudi
          </button>
        </div>
      </div>
    </div>
  );
}
